import { useNavigate } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { useAuthStore } from '../../../../../store';
import React, { useEffect, useState } from 'react';
import { useReceivingReportStore } from '../../../../../store/ReceivingReportStore';
import { useCreateData, useFetchData } from '../../../../../hooks';
import {
  RRS_API,
  useUploadRRFiles,
} from '../../../../../hooks/useReceivingReportHooks';
import { Currency, Resource, RRPurposeEnum } from '../../../../../lib/enums';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import { MaterialRequestRecord } from '../../../../../types/MaterialRequest';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import CustomForm from '../../../../../components/ui/CustomForm';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ReceivingReportMovementCreate from './ReceivingReportMovementCreate';

const ReceivingReportFormCreatePage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const receivingReportStore = useReceivingReportStore();
  const { user } = useAuthStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  const [fileList, setFileList] = useState<FormData | null>(null);

  const {
    mutate: mutateFile,
    isError: isFileError,
    error: fileError,
  } = useUploadRRFiles();
  const {
    mutate,
    isPending,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
    data: createdData,
  } = useCreateData(RRS_API);

  const rrPurposeOptions: BaseModel[] = Object.keys(RRPurposeEnum).map(
    (key) =>
      ({
        id: key,
        code: RRPurposeEnum[key as keyof typeof RRPurposeEnum],
        description: RRPurposeEnum[key as keyof typeof RRPurposeEnum],
      }) as BaseModel,
  );

  const currencyOptions: BaseModel[] = Object.keys(Currency).map(
    (key) =>
      ({
        id: key,
        code: Currency[key as keyof typeof Currency],
        description: Currency[key as keyof typeof Currency],
      }) as BaseModel,
  );

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const { data: vendorsData, isLoading: isVendorLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/vendors', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });
  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/receiving-report');
  };

  const handleClose = () => {
    navigate('/stock-movement/receiving-report');
  };

  useEffect(() => {
    if (isSuccessCreate) {
      if (fileList !== null) {
        mutateFile({ id: createdData.data.id, formData: fileList });
      }
      setIsInfoModalOpen(true);
    }
    if (isErrorCreate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorCreate?.response?.data.message);
    }
    if (isFileError) {
      setIsErrorModalOpen(true);
      setErrorMess(fileError.message);
    }
  }, [isSuccessCreate, isErrorCreate, isFileError]);

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const vendor: BaseModel | undefined = vendorsData?.data.find((obj) => {
      return obj.id == values.vendor;
    });

    console.log(values);
    const payload: MaterialRequestRecord = {
      ...values,
      destination: destination,
      vendor: vendor,
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      receivedDate:
        values.receivedDate === null || values.receivedDate === ''
          ? null
          : dayjs(values.receivedDate).format(dateFormat),
      movements: receivingReportStore.formData.movements || [],
    };
    mutate(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      setFileList(formData);
    }
  };

  const fields: FormField[] = [
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: receivingReportStore.formData.requiredDate,
    },
    {
      name: 'receivedDate',
      label: 'Received Date',
      type: 'datepicker',
      defaultValue: receivingReportStore.formData.receivedDate,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue:
        receivingReportStore.formData.destination != null
          ? receivingReportStore.formData.destination.id
          : user?.defaultLocation?.id,
    },
    {
      name: 'vendor',
      label: 'Vendor',
      allowClear: true,
      type: 'select',
      isLoading: isVendorLoading,
      isMultiple: false,
      values: vendorsData?.data,
      defaultValue: receivingReportStore.formData.vendor,
    },
    {
      name: 'rrPurpose',
      label: 'RR Purpose',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: rrPurposeOptions,
      defaultValue: receivingReportStore.data?.rrPurpose,
    },
    {
      name: 'invoiceNo',
      label: 'Invoice No',
      type: 'input',
      defaultValue: receivingReportStore.formData.invoiceNo,
    },
    {
      name: 'drNo',
      label: 'DR No',
      type: 'input',
      defaultValue: receivingReportStore.formData.drNo,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: receivingReportStore.formData.source,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'radio-group-button',
      defaultValue: receivingReportStore.formData.currency,
      values: currencyOptions,
    },
    {
      name: 'wtax',
      label: 'Withholding Tax',
      type: 'input',
      defaultValue: receivingReportStore.formData.wtax,
    },
    {
      name: 'discount',
      label: 'Discount',
      type: 'input',
      defaultValue: receivingReportStore.formData.discount,
    },
    {
      name: 'vat',
      label: 'VAT',
      type: 'input',
      defaultValue: receivingReportStore.formData.vat,
    },
    {
      name: 'notes',
      label: 'Remarks',
      type: 'textarea',
      defaultValue: receivingReportStore.formData.source,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: receivingReportStore.formData.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Material request'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        <div className="p-4">
          <CustomForm
            data={receivingReportStore.formData}
            formFields={fields}
            form={form}
            onFinish={onFinish}
            rowCol={3}
            layout={'vertical'}
            formName={'po_form'}
          />
        </div>
        <ReceivingReportMovementCreate />
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Receiving Report successfully created'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default ReceivingReportFormCreatePage;
